export function generateSessionId({ prefix = '', short = false } = {}) {
    // Use crypto.getRandomValues() for cryptographically secure random number generation
    const randomValues = new Uint8Array(16);
    crypto.getRandomValues(randomValues);

    // Format as UUID v4
    randomValues[6] = (randomValues[6] & 0x0f) | 0x40; // Version 4
    randomValues[8] = (randomValues[8] & 0x3f) | 0x80; // Variant bits

    const hex = Array.from(randomValues)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20, 32)
    ].join('-');
};

export const generateCustomId = () => {
    const shortUuid = crypto.randomUUID().slice(0, 3); // 'abc'
    const timestamp = Date.now();
    return `${shortUuid}_${timestamp}`;
};

export const generateCustomUUID = () => {
    return crypto.randomUUID();
}

export const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

/**
 * Downloads a file from base64 data
 * @param {string} base64Data - Base64 encoded file data (with or without data URL prefix)
 * @param {string} filename - Name of the file to download (e.g., "document.pdf")
 * @param {string} fileType - Optional file type/extension (e.g., "pdf", "jpg", "png"). If not provided, will be inferred from filename or base64 data
 * @returns {boolean} - Returns true if download was successful, false otherwise
 */

/**
 * Scrolls to the latest view point in a chat container
 * @param {Object} chatEndRef - React ref object pointing to the chat end element
 * @param {boolean} isMobile - Whether the user is on a mobile device
 * @param {boolean} instant - Whether to scroll instantly (default: false)
 * @param {boolean} smooth - Whether to use smooth scrolling (default: false)
 */
export const scrollToLatestViewPoint = (chatEndRef, isMobile, instant = false, smooth = false) => {
    const timeInterval = instant ? 200 : (isMobile ? 3800 : 3000);

    setTimeout(() => {
        if (chatEndRef?.current) {
            chatEndRef.current.scrollIntoView({
                behavior: smooth ? 'smooth' : 'auto',
                block: 'end'
            });
        } else {
            console.warn("ref is not ready yet");
        }
    }, timeInterval);
};


export const copyToClipboard = async (content) => {
    try {
        if (!content) return false;

        if (typeof content === 'string') {
            await navigator.clipboard.writeText(content);
            return true;
        }

        if (typeof content === 'object') {
            const data = {};
            if (content.text) {
                data['text/plain'] = new Blob([content.text], { type: 'text/plain' });
            }
            if (content.html) {
                data['text/html'] = new Blob([content.html], { type: 'text/html' });
            }

            if (Object.keys(data).length > 0) {
                const clipboardItem = new ClipboardItem(data);
                await navigator.clipboard.write([clipboardItem]);
                return true;
            }
        }

        return false;
    } catch (err) {
        console.error("Failed to copy:", err);
        // Fallback to text only if object copy fails
        if (typeof content === 'object' && content.text) {
            try {
                await navigator.clipboard.writeText(content.text);
                return true;
            } catch (e) {
                console.error("Fallback copy failed:", e);
            }
        }
        return false;
    }
};

/**
 * Formats indicative prompts from the API response into a structured format for UI selection
 * @param {Object} data - The raw API response containing department and daily_tasks prompts
 * @returns {Array} Formatted array of categories and prompts
 */
export const formatPrompts = (response) => {
    const rawData =
        Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.data?.prompts)
                    ? response.data.prompts
                    : [];

    const grouped = rawData.reduce((acc, item) => {
        const dept = item.department?.trim() || "Others";

        if (!acc[dept]) acc[dept] = [];
        acc[dept].push(item);

        return acc;
    }, {});

    return Object.entries(grouped).map(([deptName, prompts]) => ({
        label: deptName,
        prompts: prompts
            .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
            .map(item => item.prompt_text)
    }));
};

export const mapConversationMessages = (
    messages = [],
    {
        useRoleSuffixId = true,
        includeGraph = true,
    } = {}
) =>
    messages.flatMap((msg) => {
        const messageId = msg.message_id || generateCustomId();
        const timestamp = msg.create_time || msg.timestamp || new Date().toISOString();
        const userId = useRoleSuffixId ? `${messageId}-user` : messageId;
        const assistantId = useRoleSuffixId ? `${messageId}-assistant` : messageId;

        const userMessage = {
            role: "user",
            content: msg.query || "",
            id: userId,
            messageId,
            timestamp,
        };

        const assistantMessage = {
            role: "assistant",
            content: msg.response || "",
            id: assistantId,
            messageId,
            feedback: msg.feedback || "",
            timestamp,
            sources: msg.sources || null,
            follow_up_questions: msg.follow_up_questions || null,
        };

        const graphData = includeGraph ? (msg.graph_json || msg.graph || msg.graphs) : null;
        if (graphData) {
            try {
                assistantMessage.graph = typeof graphData === "string" ? JSON.parse(graphData) : graphData;
            } catch (error) {
                console.error("Failed to parse graph JSON:", error);
            }
        }

        return [userMessage, assistantMessage];
    });

export const formatImageData = (imageData, metadata) => {
    const metaImgData = metadata?.image_data;
    const isMetaImgString = typeof metaImgData === 'string';

    if (imageData?.base64_image || metaImgData?.base64_image || isMetaImgString) {
        return {
            ...(imageData || {}),
            ...(typeof metaImgData === 'object' ? metaImgData : {}),
            base64_image: (() => {
                let b64 = imageData?.base64_image || (isMetaImgString ? metaImgData : metaImgData?.base64_image);
                if (typeof b64 === 'string' && b64.includes(",")) b64 = b64.slice(b64.indexOf(",") + 1);
                return b64;
            })()
        };
    }
    return metaImgData || imageData;
};